import axios, { AxiosError } from "axios";

import { useToastStore } from "~/components/Shared/Toast/toastStore";
import { api, getAuthHeaders } from "../axios";
import type { ServiceResponse } from "../axios";
import type {
  FileUploadResponse,
  FirmwareVersion,
  UpdateFirmwareParams,
} from "./types";

export const updateFirmwareVersion = async ({
  firmwareId,
  firmwareVersionId,
  file,
  privacy_mode,
  private_member_ids,
}: UpdateFirmwareParams): Promise<FirmwareVersion> => {
  const { pushToast } = useToastStore.getState();

  try {
    let file_id: number | undefined;
    if (file) {
      // Step 1: Call your backend to get the upload URL and file_id
      const uploadFileData = await api.post<
        ServiceResponse<FileUploadResponse>
      >(
        "/files/upload",
        {
          name: file.name,
        },
        {
          headers: getAuthHeaders(),
        },
      );
      const { temporary_url, file: fileData } = uploadFileData.data.data;

      // Step 2: Upload to Azure Blob Storage
      await axios.put(
        temporary_url,
        file, // the raw file blob
        {
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "x-ms-blob-content-disposition": `attachment; filename="${file.name}"`,
          },
        },
      );

      // Step 3: Confirm file upload
      await api.post(
        "/files/confirm-file-upload",
        {
          file_id: fileData.id,
          status: "UPLOADED",
        },
        {
          headers: getAuthHeaders(),
        },
      );
      file_id = fileData.id;
    }
    // Step 4: Patch firmware version (no version field)
    const { data } = await api.patch<ServiceResponse<FirmwareVersion>>(
      `/firmwares/${firmwareId}/versions`,
      {
        firmware_version_id: firmwareVersionId,
        ...(file_id ? { file_id } : {}),
        privacy_mode: privacy_mode.toUpperCase(),
        private_member_ids,
      },
      {
        headers: getAuthHeaders(),
      },
    );

    return data.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      await pushToast({
        type: "error",
        title: "Error",
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        message: error.response?.data?.error?.message,
      });
    } else {
      await pushToast({
        type: "error",
        title: "Error",
        message: "An unexpected error occurred. Please try again.",
      });
    }
    throw error;
  }
};
